/**
 * Catalog route factory. CLAUDE-A spreads these under the authed route:
 *
 *   children: [...catalogRoutes(authedRoute)]
 *
 * Pages stay router-free (props in, callbacks out); this file is the only
 * place catalog code touches TanStack Router.
 */
import {
  createRoute,
  useNavigate,
  useParams,
  useSearch,
  type AnyRoute,
} from "@tanstack/react-router";

import { CatalogListPage } from "./pages/catalog-list-page";
import { ProductEditorPage } from "./pages/product-editor-page";
import { AttributesPage } from "./pages/attributes-page";
import { UomsPage } from "./pages/uoms-page";
import { ImportsListPage } from "./pages/imports-list-page";
import { ImportWizardPage } from "./pages/import-wizard-page";
import { useSession } from "@/lib/session/use-session";
import { useChannels } from "@/features/channels/queries";
import { useCustomerGroups } from "@/features/partners/queries";

import { PriceListsPage } from "./pages/price-lists-page";
import { TaxClassesPage } from "./pages/tax-classes-page";
import { useVariantOptions } from "./queries";
import { CATALOG_PERMISSIONS as P, CatalogPermissionBoundary } from "./permissions";

export interface ProductEditorSearch {
  /** Deep link into the variant grid row / barcode panel. */
  variant?: string;
  /** Zero-hit scan handoff; opens Barcodes with this exact value prefilled. */
  barcode?: string;
  /** Pre-filled SKU filter when arriving from a loop/duplicate link. */
  sku?: string;
}

export interface CatalogListSearch {
  sku?: string;
  /** Permission-guarded target-selection step for a zero-hit barcode scan. */
  register_barcode?: string;
}

export interface UomsSearch {
  /** `uom_unconvertible` deep link: pre-fill the add-conversion form. */
  from?: string;
  to?: string;
}

/** Absent keys are OMITTED (never explicit undefined) per router.tsx house rules. */
function pickString(source: Record<string, unknown>, key: string): { [k: string]: string } {
  const value = source[key];
  return typeof value === "string" && value !== "" ? { [key]: value } : {};
}

function useCatalogNav() {
  const navigate = useNavigate();
  return {
    openProduct: (productId: string, variantId?: string, barcode?: string) =>
      void navigate({
        to: "/catalog/products/$productId",
        params: { productId },
        search: {
          ...(variantId === undefined ? {} : { variant: variantId }),
          ...(barcode === undefined ? {} : { barcode }),
        },
      } as never),
    openList: (sku?: string) =>
      void navigate({
        to: "/catalog/products",
        search: sku === undefined ? {} : { sku },
      } as never),
    createProduct: () => void navigate({ to: "/catalog/products/new" } as never),
    registerBarcode: (barcode: string) =>
      void navigate({ to: "/catalog/products", search: { register_barcode: barcode } } as never),
    cancelBarcodeRegistration: () =>
      void navigate({ to: "/catalog/products", search: {} } as never),
    openImports: () => void navigate({ to: "/catalog/imports" } as never),
    newImport: () => void navigate({ to: "/catalog/imports/new" } as never),
    openImport: (importId: string) =>
      void navigate({ to: "/catalog/imports/$importId", params: { importId } } as never),
    openUomPair: (from?: string, to?: string) =>
      void navigate({
        to: "/catalog/uoms",
        search: { ...(from === undefined ? {} : { from }), ...(to === undefined ? {} : { to }) },
      } as never),
  };
}

function CatalogListRoute() {
  const nav = useCatalogNav();
  const { hasPermission } = useSession();
  const search = useSearch({ strict: false }) as CatalogListSearch;
  const canReadVariants = hasPermission(P.variantRead);
  // Target selection reads the canonical variant collection before the
  // product editor performs the write, so registration needs both grants.
  const canRegisterBarcode = hasPermission(P.productUpdate) && canReadVariants;
  const page = (
    <CatalogListPage
      initialSku={search.sku}
      registrationBarcode={search.register_barcode}
      scanPlacement="topbar"
      canScan={canReadVariants}
      canRegisterBarcode={canRegisterBarcode}
      canCreateProduct={hasPermission(P.productCreate)}
      canOpenImports={hasPermission(P.importRead)}
      canReadCategories={hasPermission(P.categoryRead)}
      onOpenProduct={nav.openProduct}
      onRegisterBarcode={nav.registerBarcode}
      onCancelBarcodeRegistration={nav.cancelBarcodeRegistration}
      onOpenBarcodeRegistration={(productId, variantId, barcode) =>
        nav.openProduct(productId, variantId, barcode)
      }
      onCreateProduct={nav.createProduct}
      onOpenImports={nav.openImports}
    />
  );
  return (
    <CatalogPermissionBoundary permission={P.productRead}>
      {search.register_barcode === undefined ? (
        page
      ) : (
        <CatalogPermissionBoundary permission={P.productUpdate}>
          <CatalogPermissionBoundary permission={P.variantRead}>{page}</CatalogPermissionBoundary>
        </CatalogPermissionBoundary>
      )}
    </CatalogPermissionBoundary>
  );
}

function ProductNewRoute() {
  const nav = useCatalogNav();
  const { hasPermission } = useSession();
  return (
    <CatalogPermissionBoundary permission={P.productCreate}>
      <ProductEditorPage
        canSave
        canReadAttributes={hasPermission(P.attributeRead)}
        canManageAttributes={false}
        canReadBarcodes={hasPermission(P.barcodeRead)}
        canCreateBarcode={hasPermission(P.barcodeCreate)}
        canDeleteBarcode={hasPermission(P.barcodeDelete)}
        canReadComponents={hasPermission(P.componentRead)}
        canCreateComponent={hasPermission(P.componentCreate)}
        canDeleteComponent={hasPermission(P.componentDelete)}
        canReadUoms={hasPermission(P.uomRead)}
        canReadCategories={hasPermission(P.categoryRead)}
        canReadTaxClasses={hasPermission(P.taxClassRead)}
        canReadVariants={hasPermission(P.variantRead)}
        onCreated={(productId) => nav.openProduct(productId)}
        onOpenProduct={nav.openProduct}
        onOpenSku={(sku) => nav.openList(sku)}
        onOpenUomPair={nav.openUomPair}
      />
    </CatalogPermissionBoundary>
  );
}

function ProductEditorRoute() {
  const nav = useCatalogNav();
  const { hasPermission } = useSession();
  const params = useParams({ strict: false }) as { productId?: string };
  const search = useSearch({ strict: false }) as ProductEditorSearch;
  const page = (
    <ProductEditorPage
      key={params.productId}
      productId={params.productId}
      initialVariantId={search.variant}
      initialBarcode={search.barcode}
      canSave={hasPermission(P.productUpdate)}
      canReadAttributes={hasPermission(P.attributeRead)}
      canManageAttributes={hasPermission(P.productUpdate)}
      canReadBarcodes={hasPermission(P.barcodeRead)}
      canCreateBarcode={hasPermission(P.barcodeCreate)}
      canDeleteBarcode={hasPermission(P.barcodeDelete)}
      canReadComponents={hasPermission(P.componentRead)}
      canCreateComponent={hasPermission(P.componentCreate)}
      canDeleteComponent={hasPermission(P.componentDelete)}
      canReadUoms={hasPermission(P.uomRead)}
      canReadCategories={hasPermission(P.categoryRead)}
      canReadTaxClasses={hasPermission(P.taxClassRead)}
      canReadVariants={hasPermission(P.variantRead)}
      onCreated={(productId) => nav.openProduct(productId)}
      onOpenProduct={nav.openProduct}
      onOpenSku={(sku) => nav.openList(sku)}
      onOpenUomPair={nav.openUomPair}
    />
  );
  return (
    <CatalogPermissionBoundary permission={P.productRead}>
      {search.barcode === undefined ? (
        page
      ) : (
        <CatalogPermissionBoundary permission={P.productUpdate}>{page}</CatalogPermissionBoundary>
      )}
    </CatalogPermissionBoundary>
  );
}

function AttributesRoute() {
  const { hasPermission } = useSession();
  return (
    <CatalogPermissionBoundary permission={P.attributeRead}>
      <AttributesPage
        canCreate={hasPermission(P.attributeCreate)}
        canUpdate={hasPermission(P.attributeUpdate)}
        canDelete={hasPermission(P.attributeDelete)}
      />
    </CatalogPermissionBoundary>
  );
}

function UomsRoute() {
  const { hasPermission } = useSession();
  const search = useSearch({ strict: false }) as UomsSearch;
  return (
    <CatalogPermissionBoundary permission={P.uomRead}>
      <UomsPage
        initialFrom={search.from}
        initialTo={search.to}
        canCreate={hasPermission(P.uomCreate)}
        canUpdate={hasPermission(P.uomUpdate)}
        // Archive prefetches the current entity ETag, so the UI action needs
        // both read and delete even though the DELETE API independently
        // enforces only its canonical delete permission.
        canDelete={hasPermission(P.uomRead) && hasPermission(P.uomDelete)}
      />
    </CatalogPermissionBoundary>
  );
}

function ImportsListRoute() {
  const nav = useCatalogNav();
  const { hasPermission } = useSession();
  return (
    <CatalogPermissionBoundary permission={P.importRead}>
      <ImportsListPage
        canCreate={hasPermission(P.importCreate)}
        canDiscard={hasPermission(P.importDelete)}
        onOpenImport={nav.openImport}
        onNewImport={nav.newImport}
      />
    </CatalogPermissionBoundary>
  );
}

function ImportNewRoute() {
  const nav = useCatalogNav();
  return (
    <CatalogPermissionBoundary permission={P.importCreate}>
      <ImportWizardPage onUploaded={nav.openImport} onBackToList={nav.openImports} />
    </CatalogPermissionBoundary>
  );
}

function ImportDetailRoute() {
  const nav = useCatalogNav();
  const { hasPermission } = useSession();
  const params = useParams({ strict: false }) as { importId?: string };
  return (
    <CatalogPermissionBoundary permission={P.importRead}>
      <ImportWizardPage
        key={params.importId}
        importId={params.importId}
        canCommit={hasPermission(P.importCommit)}
        onUploaded={nav.openImport}
        onBackToList={nav.openImports}
      />
    </CatalogPermissionBoundary>
  );
}

/**
 * Route factory per the feature-module contract: every catalog page under the
 * caller's (authed) parent route. Registry names + permissions per SPEC:
 * catalog.products.list → catalog.product.read, …
 */
function TaxClassesRoute() {
  const { hasPermission } = useSession();
  return (
    <CatalogPermissionBoundary permission={P.taxClassRead}>
      <TaxClassesPage
        canCreate={hasPermission(P.taxClassCreate)}
        canUpdate={hasPermission(P.taxClassUpdate)}
        canReadAccounts={hasPermission("ledger.account.read")}
      />
    </CatalogPermissionBoundary>
  );
}

function PriceListsRoute() {
  const { hasPermission, company } = useSession();
  const variants = useVariantOptions("", hasPermission(P.variantRead));
  const channels = useChannels(hasPermission("channels.channel.read"));
  const groups = useCustomerGroups(hasPermission("partners.customer_group.read"));
  return (
    <CatalogPermissionBoundary permission={P.priceListRead}>
      <PriceListsPage
        canCreate={hasPermission(P.priceListCreate)}
        canUpdate={hasPermission(P.priceListUpdate)}
        canDelete={hasPermission(P.priceListDelete)}
        defaultCurrency={company?.base_currency ?? ""}
        variantOptions={(variants.data ?? []).map((variant) => ({
          id: variant.id,
          label: variant.sku,
        }))}
        channelOptions={(channels.data ?? []).map((channel) => ({
          id: channel.id,
          label: channel.name,
        }))}
        customerGroupOptions={(groups.data ?? []).map((group) => ({
          id: group.id,
          label: group.name,
        }))}
      />
    </CatalogPermissionBoundary>
  );
}

export function catalogRoutes(parent: AnyRoute): AnyRoute[] {
  return [
    createRoute({
      getParentRoute: () => parent,
      path: "/catalog/products",
      validateSearch: (search: Record<string, unknown>): CatalogListSearch => ({
        ...pickString(search, "sku"),
        ...pickString(search, "register_barcode"),
      }),
      component: CatalogListRoute,
    }),
    createRoute({
      getParentRoute: () => parent,
      path: "/catalog/products/new",
      component: ProductNewRoute,
    }),
    createRoute({
      getParentRoute: () => parent,
      path: "/catalog/products/$productId",
      validateSearch: (search: Record<string, unknown>): ProductEditorSearch => ({
        ...pickString(search, "variant"),
        ...pickString(search, "barcode"),
      }),
      component: ProductEditorRoute,
    }),
    createRoute({
      getParentRoute: () => parent,
      path: "/catalog/attributes",
      component: AttributesRoute,
    }),
    createRoute({
      getParentRoute: () => parent,
      path: "/catalog/uoms",
      validateSearch: (search: Record<string, unknown>): UomsSearch => ({
        ...pickString(search, "from"),
        ...pickString(search, "to"),
      }),
      component: UomsRoute,
    }),
    createRoute({
      getParentRoute: () => parent,
      path: "/catalog/price-lists",
      component: PriceListsRoute,
    }),
    createRoute({
      getParentRoute: () => parent,
      path: "/catalog/tax-classes",
      component: TaxClassesRoute,
    }),
    createRoute({
      getParentRoute: () => parent,
      path: "/catalog/imports",
      component: ImportsListRoute,
    }),
    createRoute({
      getParentRoute: () => parent,
      path: "/catalog/imports/new",
      component: ImportNewRoute,
    }),
    createRoute({
      getParentRoute: () => parent,
      path: "/catalog/imports/$importId",
      component: ImportDetailRoute,
    }),
  ] as AnyRoute[];
}
